import {
  collection, doc, query, where, orderBy, limit, startAfter,
  getDocs, getDoc, addDoc, setDoc, updateDoc, deleteDoc,
  onSnapshot, writeBatch, runTransaction,
  increment, serverTimestamp, getCountFromServer, Timestamp,
} from 'firebase/firestore'
import { db } from '../firebase/firebase'

const mapDocs = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }))
const mapDoc = (snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null)

/**
 * Centralized Firestore access. All reads/writes in the app go through
 * this class (via the domain services in src/services/) — components
 * must never import 'firebase/firestore' directly.
 *
 * `path` is a collection path; slashes address subcollections,
 * e.g. `news/${newsId}/likes`.
 *
 * Query options: { where: [[field, op, value], ...], orderBy: [[field, dir?], ...], limit: n }
 */
class FirestoreService {
  constructor(database) {
    this.db = database
  }

  collectionRef(path) {
    return collection(this.db, ...path.split('/'))
  }

  docRef(path, id) {
    return doc(this.db, ...path.split('/'), id)
  }

  /** New doc ref with an auto-generated id (for batch inserts). */
  newDocRef(path) {
    return doc(this.collectionRef(path))
  }

  buildQuery(path, opts = {}) {
    const parts = []
    for (const [field, op, value] of opts.where || []) parts.push(where(field, op, value))
    for (const ord of opts.orderBy || []) {
      const [field, dir] = Array.isArray(ord) ? ord : [ord]
      parts.push(dir ? orderBy(field, dir) : orderBy(field))
    }
    if (opts.startAfter) parts.push(startAfter(opts.startAfter))
    if (opts.limit) parts.push(limit(opts.limit))
    return parts.length ? query(this.collectionRef(path), ...parts) : this.collectionRef(path)
  }

  // ── Reads ──────────────────────────────────────────────────────────────

  async getAll(path, opts) {
    const snap = await getDocs(this.buildQuery(path, opts))
    return mapDocs(snap)
  }

  /**
   * Cursor-paginated read. `opts.startAfter`, if given, must be the raw
   * `cursor` returned by a previous call (an opaque QueryDocumentSnapshot —
   * callers should not inspect it, only pass it back in).
   * Returns { items, cursor, hasMore }; `cursor` is null once exhausted.
   */
  async getPage(path, opts = {}) {
    const snap = await getDocs(this.buildQuery(path, opts))
    const lastDoc = snap.docs[snap.docs.length - 1] || null
    return {
      items: mapDocs(snap),
      cursor: lastDoc,
      hasMore: opts.limit ? snap.docs.length === opts.limit : false,
    }
  }

  async getById(path, id) {
    const snap = await getDoc(this.docRef(path, id))
    return mapDoc(snap)
  }

  async exists(path, field, value) {
    const snap = await getDocs(this.buildQuery(path, { where: [[field, '==', value]], limit: 1 }))
    return !snap.empty
  }

  async count(path) {
    const snap = await getCountFromServer(this.collectionRef(path))
    return snap.data().count
  }

  // ── Subscriptions ──────────────────────────────────────────────────────

  /** Returns unsubscribe fn; cb receives an array of {id, ...data}. */
  subscribe(path, opts, cb, onError) {
    return onSnapshot(
      this.buildQuery(path, opts),
      snap => cb(mapDocs(snap)),
      onError || (err => console.error(`[firestore] subscribe ${path}:`, err)),
    )
  }

  /** Returns unsubscribe fn; cb receives {id, ...data} or null. */
  subscribeDoc(path, id, cb, onError) {
    return onSnapshot(
      this.docRef(path, id),
      snap => cb(mapDoc(snap)),
      onError || (err => console.error(`[firestore] subscribeDoc ${path}/${id}:`, err)),
    )
  }

  // ── Writes ─────────────────────────────────────────────────────────────

  async add(path, data) {
    const ref = await addDoc(this.collectionRef(path), data)
    return ref.id
  }

  set(path, id, data, { merge = false } = {}) {
    return setDoc(this.docRef(path, id), data, { merge })
  }

  update(path, id, data) {
    return updateDoc(this.docRef(path, id), data)
  }

  remove(path, id) {
    return deleteDoc(this.docRef(path, id))
  }

  // ── Batch / transaction ────────────────────────────────────────────────

  /**
   * Run several writes atomically. `build` receives a batch facade whose
   * methods take (path, id, data) instead of raw refs.
   */
  batch(build) {
    const b = writeBatch(this.db)
    build({
      set: (path, id, data, opts) => b.set(id ? this.docRef(path, id) : this.newDocRef(path), data, opts || {}),
      add: (path, data) => b.set(this.newDocRef(path), data),
      update: (path, id, data) => b.update(this.docRef(path, id), data),
      delete: (path, id) => b.delete(this.docRef(path, id)),
    })
    return b.commit()
  }

  transaction(fn) {
    return runTransaction(this.db, fn)
  }

  /** Deletes every document currently in a collection, in chunks of 400. */
  async deleteAll(path) {
    const docs = await this.getAll(path)
    const CHUNK = 400
    for (let i = 0; i < docs.length; i += CHUNK) {
      const chunk = docs.slice(i, i + CHUNK)
      await this.batch(b => chunk.forEach(d => b.delete(path, d.id)))
    }
    return docs.length
  }

  /**
   * Upserts documents that already carry their own `id` (e.g. a backup
   * restore), preserving those ids instead of generating new ones.
   * Chunked in batches of 400; reports progress if `onProgress` is given.
   */
  async setMany(path, docsWithId, onProgress) {
    const CHUNK = 400
    for (let i = 0; i < docsWithId.length; i += CHUNK) {
      const chunk = docsWithId.slice(i, i + CHUNK)
      await this.batch(b => chunk.forEach(({ id, ...data }) => b.set(path, id, data)))
      if (onProgress) onProgress(Math.min(i + CHUNK, docsWithId.length), docsWithId.length)
    }
  }

  // ── Field values ───────────────────────────────────────────────────────

  timestamp() { return serverTimestamp() }
  increment(n = 1) { return increment(n) }
  fromDate(date) { return Timestamp.fromDate(date) }
  isTimestamp(v) { return v instanceof Timestamp }
  timestampFromParts(seconds, nanoseconds) { return new Timestamp(seconds, nanoseconds) }
}

export const firestore = new FirestoreService(db)
export default firestore
